import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import styles from './InstallationGuide.module.css';
import { Banner, Spinner, Button } from '@mm/components/src';
import { LinkTypes, PromoterTypes } from '@mm/state/model/PromoterModel';
import { KnownSiteType, SHOPIFY_EXTENSION_INSTRUCTION } from './constants';
import CopyToClipboard from 'react-copy-to-clipboard';
import { CopyOutlined } from '@ant-design/icons';
import { Linkify } from '@mm/components';
import {
  CustomerChatInstallationGuideSlideoutMenuInstructionsType,
  EmailDetectorInstallationGuideHowToVideoLinks,
} from './CustomerChatInstallationGuideSlideoutMenuInstructions';
import { useSelector } from 'react-redux';
import { selectIsShopifyContext } from '@mm/state/selectors/viewSelectors';

type Props = {
  className?: string;
  siteType: KnownSiteType;
  promoterJSCode: string;
  promoterType: LinkTypes;
  onCopyClick?: () => void;
  onImageClick: (image: string) => void;
  usedInSlideout?: boolean;
  hideBanners?: boolean;
};

const watchVideoSvg = (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none">
    <path fill="#ADC6FF" d="M2.708 2.5h14.584v10.417H2.708V2.5Z" />
    <path fill="#597EF7" d="M7.917 5.104v5.208l4.166-2.604-4.166-2.604Z" />
    <path
      fill="#ADC6FF"
      d="m4.886 16.042 1.468-.252 1.469.252a1.558 1.558 0 0 1-2.938 0ZM7.823 15l-1.469.252L4.886 15a1.558 1.558 0 0 1 2.937 0Z"
    />
    <path
      fill="#597EF7"
      d="m7.823 15-1.469.252L4.886 15h-3.22v1.042h3.22l1.468-.253 1.469.253h10.51V15H7.823Z"
    />
    <path
      fill="#1D39C4"
      d="M7.917 15.52c0 .188-.032.355-.094.522H4.886a1.444 1.444 0 0 1-.094-.521c0-.188.031-.354.094-.521h2.937c.062.167.094.333.094.52Z"
    />
  </svg>
);

export const InstallationGuide = ({
  hideBanners,
  className,
  siteType,
  promoterType,
  promoterJSCode,
  onCopyClick,
  onImageClick = () => {},
  usedInSlideout = true,
}: Props) => {
  const [iframeLoading, setIframeLoading] = useState(true);

  const [allInstructions, setAllInstructions] =
    useState<CustomerChatInstallationGuideSlideoutMenuInstructionsType>();

  const isGetEmails = promoterType === PromoterTypes.GETEMAILS;

  const isShopify = useSelector(selectIsShopifyContext);

  const banners =
    isGetEmails && !hideBanners && !isShopify
      ? EmailDetectorInstallationGuideHowToVideoLinks
      : null;

  useEffect(() => {
    (async () => {
      const data = await await import(
        /* webpackChunkName: "CustomerChatInstallationGuideSlideoutMenuInstructions" */ // @ts-ignore
        './CustomerChatInstallationGuideSlideoutMenuInstructions'
      );
      setAllInstructions(
        isGetEmails
          ? data.EmailDetectorInstallationGuideSlideoutMenuInstructions
          : data.CustomerChatInstallationGuideSlideoutMenuInstructions,
      );
    })();
  }, []);

  let instructions = allInstructions ? allInstructions[siteType] : null;
  const banner = banners ? banners[siteType] : null;

  if (isShopify && siteType === 'Shopify') {
    instructions = SHOPIFY_EXTENSION_INSTRUCTION;
  }

  if (isShopify && siteType === 'Other' && allInstructions) {
    instructions = allInstructions?.['Shopify'];
  }

  const content =
    !!instructions &&
    instructions.map((item, i) => {
      const step = typeof item === 'string' ? item : item.step;
      const image = typeof item === 'string' ? null : item.image;
      const substeps = typeof item === 'string' ? null : item.substeps;
      const includeCode = typeof item === 'string' ? null : item.includeCode;
      return (
        <>
          <div key={i} className={styles.step}>
            <div className={styles.step_numeral}>
              {i + 1}

              <span className={styles.step_line} />
            </div>
            <div className={styles.step_text}>{step}</div>
            {includeCode && (
              <>
                <div />
                <div className={styles.codeContainer}>
                  <textarea
                    readOnly
                    className={classNames(styles.codeBlock, {
                      [styles.smallCodeBlock]: isGetEmails,
                    })}
                    value={promoterJSCode}
                    onFocus={e => e.target.select()}
                  />
                  <CopyToClipboard text={promoterJSCode}>
                    <Button
                      leftIcon={<CopyOutlined />}
                      size="sm"
                      css={{ container: styles.codeBlockCopyButton }}
                      onClick={onCopyClick}
                    >
                      {!promoterJSCode ? 'Create & Copy Code' : 'Copy Code'}
                    </Button>
                  </CopyToClipboard>
                </div>
              </>
            )}
            {image && (
              <>
                <div />
                <img
                  className={styles.step_image}
                  onClick={() => onImageClick(image)}
                  src={image}
                />
              </>
            )}
          </div>
          {substeps && (
            <div className={styles.substeps}>
              {substeps.map((substep, substepIdx) => (
                <div key={substep} className={styles.step}>
                  <div className={styles.step_numeral}>
                    {String.fromCharCode('a'.charCodeAt(0) + substepIdx)}
                  </div>
                  <div className={styles.step_text}>
                    <Linkify>{substep}</Linkify>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      );
    });

  return (
    <div className={classNames(className, styles.container)}>
      {banner && (
        <div className={styles.banner}>
          <Banner
            css={{
              container: styles.bannerContainer,
              header: styles.bannerHeader,
              collapsedHeader: styles.collapsedHeader,
              content: styles.bannerContent,
            }}
            withHeaderArrowIcon
            showCloseIcon={false}
            withBannerHeader
            headerText={
              <span className={styles.bannerHeaderText}>
                {watchVideoSvg}
                {banner?.title}
              </span>
            }
            withLeftArea={false}
            expandedOnMount={true}
            usedInSlideout={usedInSlideout}
          >
            <>
              {banner?.iframe && (
                <div className={styles.iframe}>
                  <iframe
                    width="380"
                    height="260"
                    loading="lazy"
                    onLoad={() => setIframeLoading(false)}
                    frameBorder={0}
                    src={banner.iframe}
                    title="YouTube video player"
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                  {iframeLoading && (
                    <span className={styles.iframeLoader}>
                      <Spinner standalone />
                    </span>
                  )}
                </div>
              )}
            </>
          </Banner>
        </div>
      )}
      <div className={styles.description}>
        {isGetEmails
          ? ''
          : 'Take the following steps to install a chat widget on your website:'}
      </div>
      {content}
    </div>
  );
};
